import React, { useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from 'src/shared/components/ui/tooltip';
import { cn } from 'src/shared/helpers/utils';

type TruncatedTextProps = {
    text: string;
    tooltipText?: string;
    className?: string;
    'data-testid'?: string;
    lines?: 1 | 2;
    showTooltip?: boolean;
    forceTooltip?: boolean;
};

const TruncatedText = ({
    text,
    tooltipText,
    className,
    'data-testid': dataTestId,
    lines = 1,
    showTooltip = true,
    forceTooltip = false,
}: TruncatedTextProps) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            setIsOpen(false);
            return;
        }
        if (forceTooltip) {
            setIsOpen(true);
            return;
        }
        const el = spanRef.current;
        if (!el) return;
        const truncated = lines === 2 ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth;
        if (truncated) setIsOpen(true);
    };

    const span = (
        <span ref={spanRef} className={cn(lines === 2 ? 'line-clamp-2' : 'truncate block', className)} data-testid={dataTestId}>
            {text}
        </span>
    );

    if (!showTooltip) return span;

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
                <TooltipTrigger asChild>{span}</TooltipTrigger>
                <TooltipPortal>
                    <TooltipContent side='top' className='max-w-sm break-words'>
                        {tooltipText ?? text}
                    </TooltipContent>
                </TooltipPortal>
            </Tooltip>
        </TooltipProvider>
    );
};

export default TruncatedText;
